/**
 * 인력 id로 키를 잡는 워크스페이스 자격. 사람·팀 레코드는 카탈로그에 둔다.
 */

import { useSyncExternalStore } from "react";
import {
  DIRECTORY_SELF_ID,
  changeDirectoryRole,
  grantOf,
  readGrant,
  removeDirectoryGrant,
  type DirectoryGrant,
  type DirectoryRole,
} from "./directory";

const KEY = "workspace-directory-v1";

type GrantMap = Record<string, DirectoryGrant>;

function read(): GrantMap {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const next: GrantMap = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (id === DIRECTORY_SELF_ID) continue;
      const grant = readGrant(value);
      if (grant) next[id] = grant;
    }
    return next;
  } catch {
    return {};
  }
}

let grants: GrantMap = read();
let snapshot: GrantMap = grants;
const listeners = new Set<() => void>();

function emit() {
  snapshot = { ...grants };
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY, JSON.stringify(grants));
  for (const listener of listeners) listener();
}

export function resetDirectoryGrants(): void {
  grants = {};
  if (typeof localStorage !== "undefined") localStorage.removeItem(KEY);
  emit();
}

export function getDirectoryGrants(): GrantMap {
  return snapshot;
}

export function subscribeDirectoryGrants(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDirectoryGrants(): GrantMap {
  return useSyncExternalStore(subscribeDirectoryGrants, getDirectoryGrants, getDirectoryGrants);
}

export function putDirectoryGrant(resourceId: string, grant: DirectoryGrant): boolean {
  if (resourceId === DIRECTORY_SELF_ID || grant.role === "owner") return false;
  grants = { ...grants, [resourceId]: grant };
  emit();
  return true;
}

/** 소유자 행과 소유자로의 변경은 거절한다. */
export function setDirectoryRole(resourceId: string, role: DirectoryRole): boolean {
  if (resourceId === DIRECTORY_SELF_ID) return false;
  const current = grantOf(grants, resourceId);
  const next = changeDirectoryRole(current, role);
  if (!next) return false;
  if (next === current) return true;
  grants = { ...grants, [resourceId]: next };
  emit();
  return true;
}

/** 인력은 지우지 않고 자격만 제거로 둔다. */
export function removeDirectoryMember(resourceId: string): boolean {
  if (resourceId === DIRECTORY_SELF_ID) return false;
  const current = grantOf(grants, resourceId);
  const next = removeDirectoryGrant(current);
  if (!next || next === current) return false;
  grants = { ...grants, [resourceId]: next };
  emit();
  return true;
}

/** 초대 중일 때만 다시 보낸 시각을 남긴다. */
export function resendDirectoryInvite(resourceId: string, now = new Date().toISOString()): boolean {
  const current = grantOf(grants, resourceId);
  if (current.role === "owner" || current.status !== "invited") return false;
  grants = { ...grants, [resourceId]: { ...current, lastActive: now } };
  emit();
  return true;
}
